import React, { useState, useMemo, useEffect, useRef } from 'react';

import moment from 'moment';

import { useAppContext } from 'services/context';
import { useToggle, useTranslation } from 'services/hooks';
import { COURSE_TYPES, FILE_TYPES, MEETING_STATUS, NOTIFICATION_TYPES, ROLES, ROUTES } from 'constants/index';

import { useNavigate } from 'react-router-dom';

import Appointment from 'components/Calendar/Appointment';
import Button from 'components/Button';
import Container from 'components/Container';
import Calendar from 'components/Calendar';
import DatePicker from 'components/Input/DatePicker';
import Input from 'components/Input';
import Icon from 'components/Icon';
import Select from 'components/Input/Select';
import Table from 'pages/ManagementPanel/Table';
import TextArea from 'components/Input/TextArea';
import Tooltip from 'components/Tooltip';
import UserDetails from 'pages/ManagementPanel/UserDetails';
import Modal from 'components/Modal';
import Notification from 'components/Notification';
import AudioPlayer from 'components/AudioPlayer';
import VideoPlayer from 'components/VideoPlayer';
import { saveArrayAsCSV, getDateValues, validateMeetingValues, capitalizeFirstLetter } from 'services/utils';
import './style.css';

const _searchFormData = {
	fname: '',
	lname: '',
	email: '',
	courseId: '',
	classId: '',
	courseStatusId: '',
	coordinatorStatusId: '',
	teacherId: '',
	saleDateFrom: '',
	saleDateTill: '',
	externalLeadId: '',
};

const ManagementPanel = () => {
	const { user, Api, addNotification, ddls } = useAppContext();

	const endDateRef = useRef();
	const toolTipRef = useRef();
	const formRef = useRef();
	const selectRef = useRef();

	const navigate = useNavigate();

	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'management_panel';
	const VALIDATIONS_TRANSLATION_KEY = 'validations';

	const MANAGEMENT_VIEW = {
		SEARCH: 'search',
		MATERIALS: 'materials',
	};

	//FORM RESULTS
	const [searchResults, setSearchResults] = useState(undefined);

	//CALENDAR MEETINGS RESULTS
	const [calendarMeetings, setCalendarMeetings] = useState([]);
	const [upcomingAppointments, setUpcomingAppointments] = useState([]);
	const [localUpcomingAppointmentsToInsert, setLocalUpcomingAppointmentsToInsert] = useState([]);
	const [teacherCourseMaterials, setTeacherCourseMaterials] = useState([]);
	const [selectedChapter, setSelectedChapter] = useState({});
	const [selectedMaterial, setSelectedMaterial] = useState({});

	//SELECTED STUDENT AND DATE FOR STATES AND STATUSES MANIPULATION
	const [selectedStudent, setSelectedStudent] = useState(undefined);
	const [currentDate, setCurrentDate] = useState(undefined);

	//MODALS STATES
	const [currentView, setCurrentView] = useState(MANAGEMENT_VIEW.SEARCH);
	const [statusesModalVisible, setStatusesModalVisible] = useState(false);
	const [teacherModalVisible, setTeacherModalVisible] = useState(false);
	const [scheduleMultipleClasses, setScheduleMultipleClassesModalToggle] = useToggle(false);
	const [pdfToDisplay, setPdfToDisplay] = useState(undefined);
	const [audioToDisplay, setAudioToDisplay] = useState(undefined);
	const [videoToDisplay, setVideoToDisplay] = useState(undefined);

	//HISTORY STATES
	const [currentCourseStatusHistory, setCurrentcourseStatusHistory] = useState(undefined);
	const [currentCoordinatorStatusHistory, setCurrentCoordinatorStatusHistory] = useState(undefined);

	//MEETING STATUS STATES
	const [localMeetingStatus, setLocalMeetingStatus] = useState(null);
	const [multipleLocalMeetingStatuses, setMultipleLocalMeetingStatuses] = useState([]);

	const isTeacher = useMemo(() => user.roleId === ROLES.Teacher, [user]);
	const isTeacherTeamLeader = useMemo(() => user.roleId === ROLES.TeacherTeamLeader, [user]);
	const isAdmin = useMemo(() => user.roleId === ROLES.Admin, [user]);

	//DDLS AND FILTERS FORM

	const [searchFormData, setSearchFormData] = useState(_searchFormData);

	//TABLES COLUMNS AND HEADERS
	const searchFormDataColumns = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						//running number - rows counter
						Header: '#',
						// accessor: 'userId',
						Cell: row => <>{row.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_fname_column`),
						accessor: 'fname',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_lname_column`),
						accessor: 'lname',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_email_column`),
						accessor: 'email',
						Cell: row => <a href={`mailto:${row.value}`}>{row.value}</a>,
					},

					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_column`),
						accessor: 'courseName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_status_column`),
						accessor: 'courseStatusName',
						Cell: row => (
							<div
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_status_column_title`)}
								onClick={() => _handleCellCallback(row)}
							>
								{row.value}
							</div>
						),
					},
					// {
					// 	Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_activation_code_column`),
					// 	accessor: 'activationCode',
					// },
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_password_column`),
						accessor: 'userPwd',
						Cell: row => <span onClick={e => _copyToClipboard(row.value, e)}>{row.value}</span>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_phone_column`),
						accessor: 'phone',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_purchase_date_column`),
						accessor: 'purchaseDate',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_coordinator_status_column`),
						accessor: 'coordinatorStatusName',
						Cell: row => (
							<div
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_coordinator_status_column_title`)}
								onClick={() => _handleCellCallback(row)}
							>
								{row.value}
							</div>
						),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_teacher_column`),
						accessor: row => `${row.teacherFname || ''} ${row.teacherLname || ''}`,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_meeting_date_column`),
						accessor: row => `${row.meetingDateFrom || ''} ${row.meetingDateTill || ''}`,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_column`),
						accessor: row => `${row.className || ''}`,
					},
				],
			},
		];
	}, [getTranslation]);

	const statusHistoryColumns = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_history_from_column`),
						accessor: 'fromStatusName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_history_to_column`),
						accessor: 'toStatusName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_history_remark_column`),
						accessor: 'remark',
						Cell: row => (
							<div className="flex" title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_history_remark_column`)}>
								<div className="value">{row.value}</div>
								{row.value && <Icon icon="copyIcon" onClick={e => _copyToClipboard(row.value, e)} />}
							</div>
						),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_history_changed_by_column`),
						accessor: 'changedByUserFname',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_history_date_column`),
						accessor: 'dateCreated',
					},
				],
			},
		];
	}, [getTranslation]);

	// UPCOMING APPOINTMENTS TABLE HEADERS
	const upcomingAppointmentsTableHeaders = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						Header: '#', //running number - rows counter
						Cell: row => (
							<>{`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_upcoming_appointments_session_column`)} ${row?.row?.index + 1}`}</>
						),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_upcoming_appointments_date_column`),
						accessor: row => (row?.meetingDateTimeFrom ? `${row?.meetingDateTimeFrom}` : ` ${row?.startTime} ${row?.date}`),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_upcoming_appointments_remark_column`),
						accessor: 'meetingRemark',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_upcoming_appointments_status_column`),
						accessor: row => {
							return (
								<div className="meeting_status">
									<Select
										options={ddls?.meetingStatus?.options}
										defaultValue={row.meetingStatusId} //Arrived
										onChange={e => _handleUpdateMeetingStatus(e, row.meetingId)}
										disabled={!row.meetingId}
									/>
									{/* <Button
										small
										startIcon="sentMessageIcon"
										extraClassName="save_btn"
										iconColor="white"
										iconSize="12"
										onClick={saveUpdatedMeeting}
										disabled={!row.meetingId || !localMeetingStatus || localMeetingStatus.meetingId !== row.meetingId}
									/> */}
								</div>
							);
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_upcoming_appointments_delete_column`),
						accessor: row => {
							return (
								<div
									className="flex"
									title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_upcoming_appointments_delete_column_title`)}
									disabled={row.meetingId}
									onClick={() => !row.meetingId && _handleDeleteLocalClassMeeting(row)}
								>
									<Icon icon="trashIcon" color={`${row.meetingId ? '#c4c4c4' : ''}`} />
								</div>
							);
						},
					},
				],
			},
		];
	}, [ddls.meetingStatus, upcomingAppointments, localMeetingStatus, getTranslation]);

	//DDLS SEARCH FORM Memoized options values
	const searchFormCourseNamesOptions = useMemo(() => ddls.courses, [ddls]);

	const searchFormCourseStatusOptions = useMemo(() => {
		if (!searchFormData.courseId) return ddls.courseStatus;
		const options = ddls?.courseStatus?.options?.filter(option => option.courseId === parseInt(searchFormData.courseId));
		return { ...ddls.courseStatus, options };
	}, [ddls, searchFormData.courseId]);

	const searchFormCoordinatorStatusOptions = useMemo(() => {
		if (!searchFormData.courseId) return ddls.coordinator;
		const options = ddls?.coordinator?.options?.filter(option => option.courseId === parseInt(searchFormData.courseId));
		return { ...ddls.coordinator, options };
	}, [ddls, searchFormData.courseId]);

	//return All Teachers ddl/filter the teachers without duplicates (if there is teacher with same value but diffrent courseId)
	const searchFormTeacherIdOptions = useMemo(() => {
		const options = ddls?.courseTeachers?.options?.filter(option => option.courseId === parseInt(searchFormData.courseId));
		if (!searchFormData.courseId) {
			const distinctTeachers = ddls?.courseTeachers?.options.filter(
				(obj, index, self) => index === self.findIndex(el => el.value === obj.value)
			);
			return { ...ddls.courseTeachers, options: distinctTeachers };
		}
		return { ...ddls.courseTeachers, options };
	}, [ddls, searchFormData.courseId]);

	//returns all classes when course name IELTS is selected
	const searchFormClassNameOptions = useMemo(() => {
		if (!searchFormData.courseId) return ddls.classes;

		const options = ddls?.classes?.options?.filter(option => option.courseId === parseInt(searchFormData.courseId));

		return { ...ddls.classes, options };
	}, [ddls, searchFormData.courseId]);

	const teacherOptions = useMemo(() => {
		if (!selectedStudent?.courseId) return ddls.courseTeachers;
		const options = ddls?.courseTeachers?.options?.filter(option => option.courseId === parseInt(selectedStudent.courseId));
		return { ...ddls.courseTeachers, options };
	}, [ddls, selectedStudent]);

	//MODALS DEPENDENT SELECT OPTIONS
	const statusesModalCoordinatorStatusOptions = useMemo(() => {
		let assignToTeacherStatusId = [7, 15, 23]; //Coordinator Status Assign to teacher
		let options;
		if (!selectedStudent?.courseId) return ddls.coordinator;
		if (isTeacherTeamLeader)
			options = ddls?.coordinator?.options?.filter(
				option => option.courseId === selectedStudent?.courseId && assignToTeacherStatusId.includes(option.value)
			);
		else {
			options = ddls?.coordinator?.options?.filter(option => option.courseId === selectedStudent?.courseId);
		}
		return { ...ddls.coordinator, options };
	}, [ddls, selectedStudent]);

	//ALLOW TO SCHEDULE A MEETING FOR STUDENT
	const allowedStatusForScheduleAppointment = useMemo(() => {
		//Code Sent 6 14 22  ,Assign to teacher 7 15 23 , Service check 8 16 24
		if (!selectedStudent?.coordinatorStatusId) return false;
		const isAllowed = ddls?.allowedStatusForAppointment?.options?.some(option => option.value === selectedStudent.coordinatorStatusId);
		return isAllowed;
	}, [selectedStudent]);

	// //SELECTED STUDENT DERIVED COURSE
	// const selectedStudentCourse = useMemo(() => {
	// 	if (!selectedStudent || !ddls) return null;
	// 	const _selectedStudentCourse = ddls?.courses?.options?.find(({ value }) => value === selectedStudent.courseId);
	// 	if (!_selectedStudentCourse) return null;

	// 	const { courseTypeId, description, value } = _selectedStudentCourse;
	// 	return { courseId: value, courseTypeId, description };
	// }, [ddls, selectedStudent]);

	const upcomingAppointmentsCounter = useMemo(() => {
		return upcomingAppointments.filter(
			appointment => appointment.meetingStatusId === MEETING_STATUS.Arrived || !appointment.meetingStatusId
		).length; //"Not arrived" status is not counted
	}, [upcomingAppointments]);

	//UPDATE STATUSES PER SELECTED STUDENT
	useEffect(() => {
		(async () => {
			if (!selectedStudent) return;
			const { courseId, recordId, userId } = selectedStudent;
			const [coordinatorHistory, courseHistory] = await Promise.all([
				Api.GetCoordinatorStatusHistory(courseId, recordId, userId),
				Api.GetCourseStatusHistory(courseId, recordId, userId),
			]);
			setCurrentcourseStatusHistory(courseHistory);
			setCurrentCoordinatorStatusHistory(coordinatorHistory);
		})();
	}, [selectedStudent]);

	//GET STUDENT AND TEACHER MEETINGS
	useEffect(() => {
		(async () => {
			if (!selectedStudent) return;
			const { userId, teacherId, recordId } = selectedStudent;
			if (!userId || !recordId) return;
			const meetings = await Api.GetStudentAndTeacherMeetings(userId, teacherId, recordId);
			if (meetings) {
				setCalendarMeetings(meetings.meetings);
				setLocalUpcomingAppointmentsToInsert([]);
				setUpcomingAppointments(meetings.studentAndTeacher);
			}
		})();
	}, [teacherModalVisible, selectedStudent, statusesModalVisible]);

	//DISPLAY COURSE MATERIAL FOR TEACHER BY FILE TYPE
	const renderTeacherMaterialsDisplay = useMemo(() => {
		if (pdfToDisplay)
			return (
				<div className="pdf">
					<object data={pdfToDisplay + '#view=FitH'} type="application/pdf" width="100%" height="100%">
						{/* <UnsupportedFile linkToDisplay={pdfToDisplay} /> */}
					</object>
				</div>
			);
		if (audioToDisplay) {
			return (
				<div className="audio">
					<AudioPlayer src={audioToDisplay} />
				</div>
			);
		}
		if (videoToDisplay) {
			return (
				<div className="video">
					<VideoPlayer src={videoToDisplay} />
				</div>
			);
		}
		if (selectedChapter && selectedChapter?.chapterBodyText) {
			return (
				<div
					className="Chapter"
					dangerouslySetInnerHTML={{
						__html: selectedChapter && selectedChapter?.chapterBodyText,
					}}
				></div>
			);
		} else return <div className="choose_chapter">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_please_choose_chapter`)}</div>;
	}, [pdfToDisplay, selectedChapter, audioToDisplay, videoToDisplay, getTranslation]);

	const _handleModalBackHome = () => {
		setScheduleMultipleClassesModalToggle();
		setMultipleLocalMeetingStatuses([]);
	};

	//when a table cell has been clicked
	const _handleCellCallback = async cell => {
		const selectedUser = cell?.row?.original;
		if (!selectedUser) return;

		setSelectedStudent(selectedUser);
		setStatusesModalVisible(true);
	};

	// Meeting Status onChange
	const _handleUpdateMeetingStatus = ({ target }, selectedMeetingId) => {
		if (!target.value) return;

		const meeting = upcomingAppointments?.find(({ meetingId }) => meetingId === selectedMeetingId);
		meeting.meetingStatusId = parseInt(target.value);
		meeting.isStatusUpdate = true; //if this is true - the backend knows that the update call came from the status function
		const { date, meetingId, meetingRemark, meetingStatusId, startTime, endTime, isStatusUpdate } = meeting;
		const updatedMeeting = { date, meetingId, meetingRemark, meetingStatusId, startTime, endTime, isStatusUpdate };
		//saves all the changed meetings in array to update them.
		setLocalMeetingStatus(updatedMeeting);

		//case for multiple meetings changing status
		if (multipleLocalMeetingStatuses.length > 0) {
			const releventMeeting = multipleLocalMeetingStatuses.find(({ meetingId }) => meetingId === selectedMeetingId);
			if (releventMeeting) {
				let meetingsClone = [...multipleLocalMeetingStatuses];
				meetingsClone = meetingsClone.filter(meet => meet.meetingId !== selectedMeetingId);
				setMultipleLocalMeetingStatuses(meetingsClone);
				return;
			}
		}
		setMultipleLocalMeetingStatuses(prev => [...prev, updatedMeeting]);
	};
	// Meeting Status Update (Save Button)
	const saveUpdatedMeeting = () => {
		localMeetingStatus
			? _handleUpdateMeeting(localMeetingStatus)
			: addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_status_validation_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
			  );

		setLocalMeetingStatus(null);
		setMultipleLocalMeetingStatuses([]);
	};

	//update when user wants to change multiple meetings statuses
	const _handleUpdateMultipleMeetingsStatuses = async () => {
		if (!multipleLocalMeetingStatuses) return;
		const { teacherId, recordId } = selectedStudent;
		const responseArray = [];
		responseArray.meetings = [...multipleLocalMeetingStatuses];
		responseArray.recordId = recordId;
		responseArray.teacherId = teacherId;
		const res = await Api.UpdateMultipleMeetings(responseArray.meetings, responseArray.recordId, responseArray.teacherId);
		if (res.isUpdate) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
			setMultipleLocalMeetingStatuses([]);
			return;
		}
		addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
	};

	//UI EVENT CALLBACKS
	const _handleExportToExcel = () => saveArrayAsCSV('Galway_search_results_' + new Date().toLocaleString(), searchResults);

	const _handleSearchSubmit = async e => {
		try {
			if (e !== undefined) {
				//clear results on real user search
				e?.preventDefault();
				setSearchResults(undefined);
			}
			//replace empty strings to null
			const data = { ...searchFormData };
			for (const key in data) {
				if (data.hasOwnProperty(key)) {
					data[key] = data[key]?.trim().replace(/  +/g, ' '); //remove all extra whitespaces
					if (!data[key]) data[key] = null;
				}
			}
			data.roleId = user.roleId;
			const res = await Api.SearchCustomers(data);
			if (res && res.length > 0) setSearchResults(res);
			else setSearchResults([]);
		} catch (error) {
			console.warn(error);
		}
	};

	const _handleChangeInput = (value, key) => {
		const _formData = { ...searchFormData };
		//clear dependent states and inputs
		if (key === 'courseId' && value === null) {
			_formData['courseStatusId'] = '';
			_formData['coordinatorStatusId'] = '';
			_formData['classId'] = '';
		} else if (key === 'courseId' && value !== null) {
			_formData['classId'] = '';
			if (searchFormData?.courseId && selectRef.current) {
				selectRef.current.selectedIndex = 0;
				_formData['courseStatusId'] = '';
				_formData['coordinatorStatusId'] = '';
			}
		} else if (key === 'saleDateFrom' && value === null) {
			endDateRef.current.value = null;
			_formData['saleDateTill'] = '';
		}

		_formData[key] = value;
		setSearchFormData(_formData);
	};

	//CREATE , UPDATE DELETE  TEACHER ,STUDENT MEETINGS AND STATUSES
	const _handleUpdateStatuses = async e => {
		e?.preventDefault();

		const form = new FormData(e.target);
		const statuses = Object.fromEntries(form.entries());
		statuses.studentId = selectedStudent.userId;
		statuses.courseId = selectedStudent.courseId;
		statuses.recordId = selectedStudent.recordId;
		const res = await Api.UpdateStudentStatusesHistory(statuses);
		if (res) {
			//update selected student statuses - or use old fallbacks
			setSelectedStudent({
				...selectedStudent,
				coordinatorStatusId: parseInt(statuses.coordinatorStatusId || selectedStudent.coordinatorStatusId),
				courseStatusId: parseInt(statuses.courseStatusId || selectedStudent.courseStatusId),
			});

			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
			_handleSearchSubmit();
		} else {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	const _handleUpdateTeacher = async e => {
		try {
			e?.preventDefault();
			const form = new FormData(e.target);
			const teacherAndUser = Object.fromEntries(form.entries());
			teacherAndUser.recordId = selectedStudent.recordId;
			const updated = await Api.UpdateStudentCourseTeacher(teacherAndUser);
			if (updated) {
				const updatedUser = { ...selectedStudent, teacherId: parseInt(teacherAndUser.teacherId) };
				setSelectedStudent(updatedUser);
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
			} else addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		} catch (error) {
			console.warn(error);
		}
	};

	const _handleInsertMeeting = async e => {
		e?.preventDefault();
		const form = new FormData(e.target);
		const meeting = Object.fromEntries(form.entries());

		if (!meeting.startTime || !meeting.endTime) {
			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_time_validation_message`),
				NOTIFICATION_TYPES.ERROR,
				8000
			);

			return;
		}

		const _meetingDate = currentDate.format('DD-MM-YYYY');
		const existOnUpcoming = _meetingValidation(meeting, _meetingDate);
		if (existOnUpcoming) return;

		meeting.date = _meetingDate;
		meeting.recordId = selectedStudent.recordId;
		meeting.teacherUserId = selectedStudent.teacherId;

		if (user.roleId === ROLES.Coordinator) meeting.coordintaorUserId = user.userId; //add user id if the coordintaor sets the meeting

		const response = await Api.InsertMeeting(meeting);
		if (response.available === false) {
			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_available_validation_message`),
				NOTIFICATION_TYPES.ERROR,
				8000
			);

			return;
		}
		if (response.isInsert && response.meeting) {
			const updatedMeetings = [...calendarMeetings, response.meeting];
			setCalendarMeetings(updatedMeetings);
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
		} else {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	const _handleInsertMultipleMeetings = async () => {
		try {
			//Admin can add more than 8 meetings
			if (upcomingAppointmentsCounter?.length > 8 && user.roleId !== ROLES.Admin) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_count_validation_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
			const addedMeetingsToSend = upcomingAppointments?.filter(meeting => {
				return !meeting.meetingId;
			});

			//if the user didnt added new meeting. / will work also when double click on button
			if (!addedMeetingsToSend) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_date_validation_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
				);
				return;
			}

			if (!selectedStudent) return;
			const { teacherId, recordId, userId } = selectedStudent;
			if (userId && !teacherId) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_teacher_validation_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
			const { isInsert, available, meetings } = await Api.InsertMultipleMeetings(addedMeetingsToSend, recordId, teacherId);
			if (available === false) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_date_validation_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}

			if (isInsert && meetings) {
				if (!userId || !teacherId || !recordId) return;
				const meetings = await Api.GetStudentAndTeacherMeetings(userId, teacherId, recordId);
				setCalendarMeetings(meetings.meetings);
				setUpcomingAppointments(meetings.studentAndTeacher);
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);

				setLocalUpcomingAppointmentsToInsert([]);
			} else {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			}
		} catch (error) {
			console.warn(error);
		}
	};

	const _handleDeleteMeeting = async meetingId => {
		const isDeleted = await Api.DeleteMeeting(meetingId);
		if (isDeleted) {
			const updatedMeetings = calendarMeetings.filter(meeting => meeting.meetingId !== meetingId);
			const _upcomingAppointments = upcomingAppointments?.filter(meeting => meeting.meetingId !== meetingId);
			_upcomingAppointments && setUpcomingAppointments(_upcomingAppointments);
			setCalendarMeetings(updatedMeetings);
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_delete_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
		} else {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_delete_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	const _handleUpdateMeeting = async meeting => {
		if (!meeting) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meeting_validation_message`), NOTIFICATION_TYPES.ERROR, 8000);

			return false;
		}

		const { valid, message, translationkey } = validateMeetingValues(null, null, meeting);

		if (!valid) {
			//TRANSLATIONS
			addNotification(
				getTranslation(VALIDATIONS_TRANSLATION_KEY, translationkey)
					?.replace('[startTime]', meeting?.startTime)
					?.replace('[endTime]', meeting?.endTime) || message,
				NOTIFICATION_TYPES.ERROR,
				8000
			);

			return false;
		}

		const response = await Api.UpdateMeeting(meeting);

		if (!response.isUpdate) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			return;
		}

		//Update local state - upcomingAppointments + calendarMeetings
		const currMeetingIndex = upcomingAppointments?.findIndex(currMeeting => currMeeting.meetingId === meeting.meetingId);
		const currCalendarIndex = calendarMeetings?.findIndex(currMeeting => currMeeting.meetingId === meeting.meetingId);

		//If the meeting was not found return from the function
		if (currMeetingIndex === -1 && currCalendarIndex === -1) return;

		let _upcomingAppointments = [...upcomingAppointments];
		let _calendarMeetings = [...calendarMeetings];

		const { date, startTime, endTime, meetingRemark, meetingStatusId } = meeting;

		//The keys that need to be updated
		const updateFields = {
			meetingDateTimeFrom: `${date} ${startTime}`,
			meetingDateTimeTill: `${date} ${endTime}`,
			startTime,
			endTime,
			meetingRemark,
		};

		if (meetingStatusId) {
			delete _upcomingAppointments[currMeetingIndex].isStatusUpdate; //This key is only relevent for the backend and does not need to be saved locally
			updateFields.meetingStatusId = meetingStatusId;
		}

		_upcomingAppointments[currMeetingIndex] = { ..._upcomingAppointments[currMeetingIndex], ...updateFields };
		_calendarMeetings[currCalendarIndex] = { ..._calendarMeetings[currCalendarIndex], ...updateFields };

		setUpcomingAppointments(_upcomingAppointments);
		setCalendarMeetings(_calendarMeetings);

		addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);

		return response.isUpdate;
	};

	const _copyToClipboard = (value, e) => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
			if (e) toolTipRef.current.Show({ x: e.pageX || -10, y: e.pageY || -10 });
		}
	};

	const _handleEnterPress = e => {
		//add Enter as submit
		const { code } = e;
		if (code === 'Enter' || code === 'NumpadEnter') _handleSearchSubmit();
	};

	//RENDER CALLBACKS
	const _handleOpenTeachersModal = async event => {
		event.preventDefault();
		setTeacherModalVisible(true);
	};
	const _handleOpenScheduleMultipleClassesModal = async event => {
		event.preventDefault();
		setStatusesModalVisible(false);
		setScheduleMultipleClassesModalToggle();
	};

	const _handleCloseAppointmentsModal = () => {
		setStatusesModalVisible(true);
		setScheduleMultipleClassesModalToggle();
		setUpcomingAppointments([]);
		setMultipleLocalMeetingStatuses([]);
	};
	const _meetingValidation = (meeting, meetingDate) => {
		//The new meeting the customer wants to schedule + The student upcoming meetings + The student's teacher meetings on the same day
		let takenMeetings;
		if (upcomingAppointments && upcomingAppointments.length > 0) {
			takenMeetings = [
				{ startTime: null, endTime: null }, // Validation to the new meeting,
				...upcomingAppointments?.filter(({ date }) => date === meetingDate),
				...calendarMeetings?.filter(({ teacherUserId, date }) => selectedStudent?.teacherId === teacherUserId && date === meetingDate),
			];
		} else {
			takenMeetings = [{ startTime: null, endTime: null }];
		}

		const existOnUpcoming = takenMeetings.some(({ startTime, endTime }) => {
			const meetingDetails = { ...meeting, date: meetingDate };
			const { valid, message, translationkey } = validateMeetingValues(startTime, endTime, meetingDetails);
			if (!valid) {
				addNotification(
					getTranslation(VALIDATIONS_TRANSLATION_KEY, translationkey)
						?.replace('[startTime]', meetingDetails?.startTime)
						?.replace('[endTime]', meetingDetails?.endTime) || message,
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return true;
			}
			return false;
		});
		return existOnUpcoming;
	};

	//HANDLE STATE SPECIFIC MEETING
	const _handleAddLocalClassMeeting = async e => {
		e?.preventDefault();
		const form = new FormData(e.target);
		const meeting = Object.fromEntries(form.entries());

		for (const key in meeting) {
			if (!meeting[key]) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_missing_fields_error_message`) + ' ' + capitalizeFirstLetter(key),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
		}

		const _meetingDate = currentDate.format('DD-MM-YYYY');
		const existOnUpcoming = _meetingValidation(meeting, _meetingDate);
		if (existOnUpcoming) return;

		meeting.date = _meetingDate;

		setUpcomingAppointments(_upcomingAppointments => {
			if (!Array.isArray(_upcomingAppointments)) return [];
			return [..._upcomingAppointments, meeting];
		});
		setLocalUpcomingAppointmentsToInsert([...localUpcomingAppointmentsToInsert, meeting]);
	};

	const _handleDeleteLocalClassMeeting = meeting => {
		const { startTime, endTime, date } = meeting;

		setUpcomingAppointments(_upcomingAppointments => {
			if (!Array.isArray(_upcomingAppointments)) return [];
			return _upcomingAppointments.filter(
				_upcoming => !(startTime === _upcoming.startTime && endTime === _upcoming.endTime && date === _upcoming.date)
			);
		});
	};

	const _clearSearchForm = () => {
		setSearchFormData(_searchFormData);
		formRef?.current?.reset();
	};

	const _renderAppointment = ({ meetingDateTimeFrom, meetingDateTimeTill, courseStatusName, userName, meetingId, meetingRemark }, idx) => {
		const dayOfMonth = moment(meetingDateTimeFrom, 'DD-MM-YYYY');

		if (!currentDate || !moment.isMoment(currentDate)) return;
		const currentDay = currentDate.isSame(dayOfMonth, 'day');
		if (!currentDay) return null;
		const [startHoursAndMinutes, startDate] = getDateValues(meetingDateTimeFrom);
		const [endHoursAndMinutes] = getDateValues(meetingDateTimeTill);

		return (
			<Appointment
				timeFrom={startHoursAndMinutes || getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointment_time_from_field`)}
				timeTo={endHoursAndMinutes || getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointment_time_to_field`)}
				title={courseStatusName}
				paragrapgh={userName}
				key={idx}
				meetingId={meetingId}
				onDelete={_handleDeleteMeeting}
				onUpdate={_handleUpdateMeeting}
				meetingRemark={meetingRemark}
				date={startDate || '14-06-2022'}
				remarkDefaultValue={meetingRemark}
			/>
		);
	};

	const _handleChangeViews = async viewState => {
		if (viewState === currentView) return;
		if (viewState === MANAGEMENT_VIEW.MATERIALS) {
			resetTeacherMaterialsStates();
			setSelectedChapter({});
			setCurrentView(MANAGEMENT_VIEW.MATERIALS);
			if (!teacherCourseMaterials.length) {
				const materials = await Api.GetAllAvailableCourses(user.userId);
				if (!materials) return;
				setTeacherCourseMaterials(materials);
			}
			return;
		}
		setCurrentView(MANAGEMENT_VIEW.SEARCH);
	};

	const resetTeacherMaterialsStates = () => {
		setAudioToDisplay(undefined);
		setVideoToDisplay(undefined);
		setPdfToDisplay(undefined);
		setSelectedChapter({});
	};

	const loadSelectedChapter = ({ chapter }) => {
		if (!chapter) return;
		resetTeacherMaterialsStates();
		setSelectedChapter(chapter);
	};

	const loadSelectedMaterial = ({ material }) => {
		resetTeacherMaterialsStates();
		if (!material) return;
		setSelectedMaterial(material);
		if (material.fileTypeID === FILE_TYPES.VIDEO) {
			setVideoToDisplay(material.fileURL);
		} else if (material.fileTypeID === FILE_TYPES.AUDIO) setAudioToDisplay(material.fileURL);
		else {
			setPdfToDisplay(material.fileURL);
		}
	};

	return (
		<div className="ManagementPanel">
			<Container>
				<div className="ManagementPanel__menu">
					<div className="ManagementPanel__menu__search" onClick={() => _handleChangeViews(MANAGEMENT_VIEW.SEARCH)}>
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_head_search_title`)}
					</div>
					{(isTeacher || isTeacherTeamLeader) && (
						<div className="ManagementPanel__menu__materials" onClick={() => _handleChangeViews(MANAGEMENT_VIEW.MATERIALS)}>
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_head_materials_title`)}
						</div>
					)}
				</div>
				{[MANAGEMENT_VIEW.SEARCH].includes(currentView) && (
					<div className="ManagementPanel__search">
						<form className="wrapper" ref={formRef} onSubmit={e => e.preventDefault()}>
							<div className="ManagementPanel__title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_title`)}</div>
							<div className="ManagementPanel__inputs_grid">
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_first_name_field`)}
									name="fname"
									onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'fname')}
									onKeyPress={_handleEnterPress}
								/>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_last_name_field`)}
									name="lname"
									onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'lname')}
									onKeyPress={_handleEnterPress}
								/>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_email_field`)}
									name="email"
									onInput={({ target }) => _handleChangeInput(target.value?.trim() || '', 'email')}
									onKeyPress={_handleEnterPress}
								/>
								<Select
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_field`)}
									placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_field`)}
									name={searchFormCourseNamesOptions?.name}
									options={searchFormCourseNamesOptions?.options || []}
									onChange={({ target }) => _handleChangeInput(target.value || null, 'courseId')}
								/>
								{(isAdmin || isTeacherTeamLeader) && (
									<Select
										label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_teacher_field`)}
										placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_teacher_field`)}
										name={searchFormTeacherIdOptions?.name}
										options={searchFormTeacherIdOptions?.options || []}
										onChange={({ target }) => _handleChangeInput(target.value || null, 'teacherId')}
									/>
								)}
								{searchFormData?.courseId && (
									<>
										<Select
											label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_status_field`)}
											placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_status_field`)}
											name={searchFormCourseStatusOptions?.name || ''}
											options={searchFormCourseStatusOptions?.options || []}
											onChange={({ target }) => _handleChangeInput(target.value || null, 'courseStatusId')}
											ref={selectRef}
										/>
										{searchFormCoordinatorStatusOptions?.options?.length > 0 && (
											<Select
												label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_coordinator_status_field`)}
												placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_coordinator_status_field`)}
												name={searchFormCoordinatorStatusOptions?.name}
												options={searchFormCoordinatorStatusOptions?.options || []}
												onChange={({ target }) => _handleChangeInput(target.value || null, 'coordinatorStatusId')}
											/>
										)}
										{searchFormClassNameOptions?.options?.length > 0 && (
											<Select
												label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_field`)}
												placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_field`)}
												name={searchFormClassNameOptions?.name}
												options={searchFormClassNameOptions?.options || []}
												onChange={({ target }) => _handleChangeInput(target.value || null, 'classId')}
											/>
										)}
									</>
								)}
								<DatePicker
									fromTo
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_date_field`)}
									startDateName="startDate"
									endDateName="endDate"
									onFromChange={value => _handleChangeInput(value, 'saleDateFrom')}
									onToChange={value => _handleChangeInput(value, 'saleDateTill')}
									endDateRef={endDateRef}
								/>
								<Input
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_lead_field`)}
									type="number"
									onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'externalLeadId')}
								/>
							</div>
							<div className="button_container">
								<div className="button_container__search">
									<Button
										title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_button`)}
										endIcon="searchIcon"
										small
										onClick={_handleSearchSubmit}
									/>
									<Button
										title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_clear_button`)}
										small
										extraClassName="form_clear"
										onClick={_clearSearchForm}
									/>
								</div>
								{user.roleId === ROLES.Admin && (
									<Button
										title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_conversations_button`)}
										small
										extraClassName="conversations"
										onClick={() => navigate(ROUTES.CONVERSATION)}
									/>
								)}
							</div>
						</form>

						{searchResults?.length > 0 && (
							<div className="wrapper search_results">
								<Button
									title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_excel_button`)}
									small
									type="secondary"
									rectangle
									onClick={_handleExportToExcel}
								/>
								<Table columns={searchFormDataColumns} data={searchResults} />
							</div>
						)}
						{searchResults?.length === 0 && (
							<h2 className="no_records">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_no_results`)}</h2>
						)}
					</div>
				)}
				{[MANAGEMENT_VIEW.MATERIALS].includes(currentView) && (
					<div className="ManagementPanel__materials">
						<div className="wrapper">
							<div className="ManagementPanel__materials__wrapper">
								<div className="ManagementPanel__materials__wrapper__side">
									{teacherCourseMaterials &&
										teacherCourseMaterials.length > 0 &&
										teacherCourseMaterials.map((m, key) => {
											return (
												<TeacherCourseItem
													key={key}
													course={m}
													selectedChapter={selectedChapter}
													selectedMaterial={selectedMaterial}
													onSelectChapter={loadSelectedChapter}
													onSelectMaterial={loadSelectedMaterial}
												/>
											);
										})}
								</div>
								<div className="seperator"></div>

								<div className="ManagementPanel__materials__wrapper__course_content">{renderTeacherMaterialsDisplay}</div>
							</div>
						</div>
					</div>
				)}
			</Container>

			<Modal
				extraClassName="statuses"
				isVisible={statusesModalVisible && !teacherModalVisible}
				setVisibility={() => setStatusesModalVisible(false)}
			>
				<UserDetails user={selectedStudent} />
				<div className="wrapper">
					<form onSubmit={_handleUpdateStatuses}>
						{/* Course Statuses Section */}
						<Select
							defaultValue={selectedStudent?.courseStatusId}
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_statuses_modal_course_status_field`)}
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_statuses_modal_course_status_field`)}
							name="courseStatusId"
							options={ddls?.courseStatus?.options?.filter(option => option.courseId === selectedStudent?.courseId) || []}
						/>
						<TextArea
							placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_statuses_modal_note_field`)}
							name="courseStatusRemark"
						/>
						{currentCourseStatusHistory?.length > 0 && (
							<Table columns={statusHistoryColumns} data={currentCourseStatusHistory} pageSizeProp={2} />
						)}
						{/* Coordinator Status Section */}
						{statusesModalCoordinatorStatusOptions?.options?.length > 0 && (
							<>
								{!isTeacher && (
									<>
										<Select
											defaultValue={selectedStudent?.coordinatorStatusId}
											label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_statuses_modal_coordinator_status_field`)}
											placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_statuses_modal_coordinator_status_field`)}
											name="coordinatorStatusId"
											options={statusesModalCoordinatorStatusOptions?.options || []}
										/>
										<TextArea
											placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_statuses_modal_note_field`)}
											name="coordinatorStatusRemark"
										/>
										{currentCoordinatorStatusHistory?.length > 0 && (
											<Table columns={statusHistoryColumns} data={currentCoordinatorStatusHistory} pageSizeProp={2} />
										)}
									</>
								)}

								{/* <Button
									small
									disabled={!allowedStatusForScheduleAppointment}
									title="Schedule a meeting"
									extraClassName="open_calendar_button"
									onClick={_handleOpenTeachersModal}
								/> */}
								<Button
									small
									disabled={!allowedStatusForScheduleAppointment}
									title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_schedule_button`)}
									extraClassName="open_calendar_button"
									onClick={_handleOpenScheduleMultipleClassesModal}
								/>
							</>
						)}
						<Button title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_save_button`)} extraClassName="save_button" small />
					</form>
				</div>
			</Modal>
			{/* <Modal
				extraClassName="appointments"
				isVisible={selectedStudent && teacherModalVisible}
				setVisibility={() => setTeacherModalVisible(false)}
			>
				<UserDetails user={selectedStudent} />
				<div className="appointments__wrapper">
					{!isTeacher && (
						<form className="update_teacher" onSubmit={_handleUpdateTeacher}>
							<Select
								name={'teacherId'}
								placeHolder="Select teacher"
								options={teacherOptions?.options || []}
								defaultValue={selectedStudent?.teacherId}
							/>
							<Button small title="Update Teacher" />
						</form>
					)}
					{isTeacher && (
						<label className="teacher_label">{`Teacher : ${selectedStudent?.teacherFname || ''}   ${
							selectedStudent?.teacherLname || ''
						}`}</label>
					)}
					<div className="calendar_wrapper">
						<Calendar
							markedDates={calendarMeetings?.map(meeting => meeting.meetingDateTimeFrom) || []}
							onDayChange={date => setCurrentDate(moment(date))}
						/>
						<div className="schedule_appointment">
							<div className="appointments_list">{calendarMeetings?.map(_renderAppointment)}</div>
							<form className="appointments_form" onSubmit={_handleInsertMeeting}>
								<DatePicker type="time" fromTo startDateName="startTime" endDateName="endTime" label="Appointment time" />
								<TextArea placeholder="Place for Zoom Link, and comment that would be sent to customer" name="meetingRemark" />
								<Button extraClassName="appointments_form__button" title="Send and Save" small />
							</form>
						</div>
					</div>
				</div>
			</Modal> */}
			{/* MULTIPLE CLASSES MEETINGS MODAL */}
			<Modal
				isVisible={scheduleMultipleClasses}
				setVisibility={_handleCloseAppointmentsModal}
				backHome={_handleModalBackHome}
				extraClassName="multiple_appointments"
				hideCloseButton
			>
				<UserDetails user={selectedStudent} />
				<div className="appointments__wrapper">
					{!isTeacher && (
						<form className="update_teacher" onSubmit={_handleUpdateTeacher}>
							<Select
								name={'teacherId'}
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_teacher_field`)}
								options={teacherOptions?.options || []}
								defaultValue={selectedStudent?.teacherId}
							/>
							<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_teacher_button`)} />
						</form>
					)}
					{isTeacher && (
						<label className="teacher_label">{`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_teacher_label`)} ${
							selectedStudent?.teacherFname || ''
						}   ${selectedStudent?.teacherLname || ''}`}</label>
					)}
					<div className="calendar_wrapper">
						<Calendar
							onDayChange={date => setCurrentDate(moment(date))}
							markedDates={calendarMeetings?.map(meeting => meeting.meetingDateTimeFrom) || []}
						/>
						<div className="schedule_appointment">
							<div className="appointments_list">{calendarMeetings?.map(_renderAppointment)}</div>
							<form className="appointments_form" onSubmit={_handleAddLocalClassMeeting}>
								<DatePicker
									type="time"
									fromTo
									startDateName="startTime"
									endDateName="endTime"
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_time_field`)}
								/>
								<TextArea
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_remark_field`)}
									placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_remark_placeholder`)}
									name="meetingRemark"
								/>
								<Button
									extraClassName="appointments_form__button"
									title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_meeting_button`)}
									small
								/>
							</form>
						</div>
					</div>
					{upcomingAppointments?.length > 0 && (
						<div className="ClassModal__table">
							<Table columns={upcomingAppointmentsTableHeaders} data={upcomingAppointments} pageSizeProp={8} />
							<Button
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_save_classes_button`)}
								small
								onClick={_handleInsertMultipleMeetings}
								disabled={
									localUpcomingAppointmentsToInsert.length === 0 || (user.roleId !== ROLES.Admin && upcomingAppointmentsCounter > 8)
								}
							/>
							<Button
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_save_meeting_statuses_button`)}
								small
								extraClassName="MultiMeetingStatuses"
								onClick={_handleUpdateMultipleMeetingsStatuses}
								disabled={multipleLocalMeetingStatuses.length <= 0}
							/>
						</div>
					)}
				</div>
			</Modal>

			<Tooltip ref={toolTipRef}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_tooltip_note`)}</Tooltip>
		</div>
	);
};

export default ManagementPanel;

const TeacherCourseItem = ({ course, onSelectChapter, onSelectMaterial, selectedChapter, selectedMaterial, defaultOpen = false }) => {
	const [isOpen, setIsOpen] = useState(!!defaultOpen);
	const [openUnitId, setOpenUnitId] = useState(null);
	const [openMaterialId, setOpenMaterialId] = useState(null);

	if (!course) return null;

	const toggleCourse = () => setIsOpen(prev => !prev);

	const handleCourseUnitOnClick = ({ unit }, e) => {
		e?.preventDefault?.();
		e?.stopPropagation?.();
		setOpenUnitId(prev => (prev === unit.id ? null : unit.id));
	};

	const handleCourseMaterialOnClick = (chapterId, e) => {
		e?.preventDefault?.();
		e?.stopPropagation?.();
		setOpenMaterialId(prev => (prev === chapterId ? null : chapterId));
	};

	const handleCourseChapterOnClick = ({ chapter, unit, material }, e) => {
		e?.preventDefault?.();
		e?.stopPropagation?.();
		if (chapter || unit) onSelectChapter && typeof onSelectChapter === 'function' && onSelectChapter({ unit, chapter });
		else if (material) onSelectMaterial && typeof onSelectMaterial === 'function' && onSelectMaterial({ material });
	};

	return (
		<div className={`TeacherCourseItem`} key={course.courseId}>
			<div className="TeacherCourseItem__course-header" onClick={toggleCourse}>
				<div className="TeacherCourseItem__course-header__text" title={course.courseName}>
					{course.courseName}
				</div>
			</div>

			<div className="TeacherCourseItem__chapter-list">
				{isOpen && course.courseTypeId === COURSE_TYPES.ESL && (
					<ul className="TeacherCourseItem__unit-item">
						<li>
							<a href={course.guideFileUrl} target="_blank">
								Syllabus
							</a>
						</li>
					</ul>
				)}
				{isOpen &&
					course?.units &&
					course.units.map(unit => (
						<ul key={unit.id} className={`TeacherCourseItem__unit-item`}>
							<li className="TeacherCourseItem__unit-item__header" onClick={e => handleCourseUnitOnClick({ unit }, e)}>
								<div className="TeacherCourseItem__unit-item__title">{unit.title}</div>
							</li>

							{openUnitId === unit.id && (
								<ul className="TeacherCourseItem__unit-item__chapters">
									{unit &&
										unit?.classMaterial?.map(material => {
											return (
												<li className="TeacherCourseItem__material-item__header" onClick={e => handleCourseChapterOnClick({ material }, e)}>
													<div className="TeacherCourseItem__material-item__title">{material.fileDisplayName}</div>
												</li>
											);
										})}
									{unit?.courseChapters?.length &&
										unit.courseChapters.map((chapter, i) => (
											<li
												key={chapter.courseChapterId || `${unit.id}-${i}`}
												className={`chapter-item ${(selectedChapter === chapter && 'selected') || ''}`}
												onClick={e => handleCourseChapterOnClick({ chapter, unit }, e)}
											>
												{chapter.chapterName || chapter.title}
											</li>
										))}
								</ul>
							)}
						</ul>
					))}
				{isOpen &&
					course?.courseChapters &&
					course?.courseChapters
						?.filter(chapter => !chapter.isFinal)
						?.map(chapter => (
							<ul key={chapter.courseChapterId} className={`TeacherCourseItem__chapter-item`}>
								<li
									className="TeacherCourseItem__chapter-item__header"
									onClick={e => handleCourseMaterialOnClick(chapter.courseChapterId, e)}
								>
									<div className="TeacherCourseItem__chapter-item__title">{chapter.chapterName}</div>
								</li>
								{openMaterialId === chapter.courseChapterId && (
									<ul className="TeacherCourseItem__unit-item__chapters">
										{chapter?.classMaterial?.length &&
											chapter.classMaterial.map((material, i) => (
												<li
													key={material.fileURL || `${i}`}
													className={`chapter-item  ${(material.fileURL === selectedMaterial.fileURL && 'selected') || ''}`}
													onClick={e => handleCourseChapterOnClick({ material }, e)}
												>
													{material.fileDisplayName}
												</li>
											))}
									</ul>
								)}
							</ul>
						))}
			</div>
		</div>
	);
};
